'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useConfig } from './ConfigProvider.jsx';
import themes from '../config/themes.json';

const ThemeContext = createContext();

/**
 * Provider para manejo de temas y design tokens
 * Aplica variables CSS dinámicamente basado en la configuración
 */
export function ThemeProvider({ children, defaultTheme = 'light' }) {
  const { theme: configTheme, getConfigValue } = useConfig();
  const [currentTheme, setCurrentTheme] = useState(defaultTheme);
  const [customTheme, setCustomTheme] = useState(null);

  /**
   * Aplicar variables CSS al documento
   */
  const applyThemeVariables = useCallback((themeData) => {
    const root = document.documentElement;
    
    // Aplicar colores
    if (themeData.colors) {
      Object.entries(themeData.colors).forEach(([key, value]) => {
        root.style.setProperty(`--sidenav-color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`, value);
      });
    }
    
    // Aplicar tipografía
    if (themeData.typography) {
      const { fontFamily, fontSize, fontWeight, lineHeight } = themeData.typography;
      
      if (fontFamily) {
        root.style.setProperty('--sidenav-font-family', fontFamily);
      }
      
      if (fontSize) {
        Object.entries(fontSize).forEach(([key, value]) => {
          root.style.setProperty(`--sidenav-font-size-${key}`, value);
        });
      }
      
      if (fontWeight) {
        Object.entries(fontWeight).forEach(([key, value]) => {
          root.style.setProperty(`--sidenav-font-weight-${key}`, value);
        });
      }
      
      if (lineHeight) {
        Object.entries(lineHeight).forEach(([key, value]) => {
          root.style.setProperty(`--sidenav-line-height-${key}`, value);
        });
      }
    }
    
    // Aplicar espaciado
    if (themeData.spacing) {
      Object.entries(themeData.spacing).forEach(([key, value]) => {
        root.style.setProperty(`--sidenav-spacing-${key}`, value);
      });
    }
    
    // Aplicar border radius
    if (themeData.borderRadius) {
      Object.entries(themeData.borderRadius).forEach(([key, value]) => {
        root.style.setProperty(`--sidenav-radius-${key}`, value);
      });
    }
    
    // Aplicar sombras
    if (themeData.shadows) {
      Object.entries(themeData.shadows).forEach(([key, value]) => {
        root.style.setProperty(`--sidenav-shadow-${key}`, value);
      });
    }
    
    // Aplicar transiciones
    if (themeData.transitions) {
      const { duration, easing } = themeData.transitions;
      if (duration) {
        root.style.setProperty('--sidenav-transition-duration', duration);
      }
      if (easing) {
        root.style.setProperty('--sidenav-transition-easing', easing);
      }
    }
  }, []);

  /**
   * Obtener datos del tema actual
   */
  const getCurrentThemeData = useCallback(() => {
    // Prioridad: tema personalizado > tema de configuración > tema predefinido
    if (customTheme) {
      return customTheme;
    }
    
    if (configTheme && Object.keys(configTheme).length > 0) {
      return configTheme;
    }
    
    return themes[currentTheme] || themes.light;
  }, [currentTheme, configTheme, customTheme]);

  /**
   * Cambiar tema
   */
  const changeTheme = useCallback((themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
      setCustomTheme(null);
      document.documentElement.setAttribute('data-theme', themeName);
    }
  }, []);

  /**
   * Aplicar tema personalizado
   */
  const applyCustomTheme = useCallback((themeData) => {
    setCustomTheme(themeData);
    setCurrentTheme('custom');
    document.documentElement.setAttribute('data-theme', 'custom');
  }, []);

  /**
   * Obtener valor específico del tema
   */
  const getThemeValue = useCallback((path, defaultValue = null) => {
    const themeData = getCurrentThemeData();
    try {
      return path.split('.').reduce((obj, key) => obj?.[key], themeData) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  }, [getCurrentThemeData]);

  /**
   * Generar tema basado en color primario
   */
  const generateThemeFromPrimary = useCallback((primaryColor) => {
    // Función simple para generar variaciones de color
    const hexToHsl = (hex) => {
      const r = parseInt(hex.slice(1, 3), 16) / 255;
      const g = parseInt(hex.slice(3, 5), 16) / 255;
      const b = parseInt(hex.slice(5, 7), 16) / 255;
      
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;
      
      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = (g - b) / d + (g < b ? 6 : 0); break;
          case g: h = (b - r) / d + 2; break;
          case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
      }
      
      return [h * 360, s * 100, l * 100];
    };
    
    const hslToHex = (h, s, l) => {
      l /= 100;
      const a = s * Math.min(l, 1 - l) / 100;
      const f = n => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color).toString(16).padStart(2, '0');
      };
      return `#${f(0)}${f(8)}${f(4)}`;
    };
    
    const [h, s, l] = hexToHsl(primaryColor);
    
    return {
      name: 'Generated Theme',
      colors: {
        primary: primaryColor,
        secondary: hslToHex(h, Math.max(s - 20, 0), Math.min(l + 20, 90)),
        background: '#ffffff',
        surface: hslToHex(h, Math.max(s - 40, 0), 98),
        text: '#1e293b',
        textSecondary: '#64748b',
        border: hslToHex(h, Math.max(s - 30, 0), 90),
        hover: hslToHex(h, Math.max(s - 35, 0), 95),
        active: primaryColor
      }
    };
  }, []);

  /**
   * Detectar preferencia de tema del sistema
   */
  const detectSystemTheme = useCallback(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }, []);

  /**
   * Alternar entre tema claro y oscuro
   */
  const toggleTheme = useCallback(() => {
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    changeTheme(newTheme);
  }, [currentTheme, changeTheme]);

  // Aplicar tema cuando cambie
  useEffect(() => {
    const themeData = getCurrentThemeData();
    applyThemeVariables(themeData);
  }, [getCurrentThemeData, applyThemeVariables]);

  // Escuchar cambios en preferencias del sistema
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        if (currentTheme === 'system') {
          const systemTheme = e.matches ? 'dark' : 'light';
          document.documentElement.setAttribute('data-theme', systemTheme);
        }
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [currentTheme]);

  const contextValue = {
    // Estado
    currentTheme,
    customTheme,
    availableThemes: Object.keys(themes),
    themeData: getCurrentThemeData(),
    
    // Métodos
    changeTheme,
    applyCustomTheme,
    getThemeValue,
    generateThemeFromPrimary,
    detectSystemTheme,
    toggleTheme,
    
    // Utilidades
    isLight: () => {
      const themeData = getCurrentThemeData();
      return !currentTheme.includes('dark') && 
             (!themeData.colors?.background || 
              themeData.colors.background === '#ffffff' || 
              themeData.colors.background.includes('f'));
    },
    
    isDark: () => {
      const themeData = getCurrentThemeData();
      return currentTheme.includes('dark') || 
             (themeData.colors?.background && 
              !themeData.colors.background.includes('f') && 
              themeData.colors.background !== '#ffffff');
    }
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook para usar el contexto de tema
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * Versión que no lanza: el contexto de tema o `null` sin ThemeProvider.
 */
export function useOptionalTheme() {
  return useContext(ThemeContext) || null;
}

/**
 * Hook para obtener colores del tema actual
 */
export function useThemeColors() {
  const { getThemeValue } = useTheme();
  
  return {
    primary: getThemeValue('colors.primary', '#2563eb'),
    secondary: getThemeValue('colors.secondary', '#64748b'),
    background: getThemeValue('colors.background', '#ffffff'),
    surface: getThemeValue('colors.surface', '#f8fafc'),
    text: getThemeValue('colors.text', '#1e293b'),
    textSecondary: getThemeValue('colors.textSecondary', '#64748b'),
    border: getThemeValue('colors.border', '#e2e8f0'),
    hover: getThemeValue('colors.hover', '#f1f5f9'),
    active: getThemeValue('colors.active', '#2563eb')
  };
}

/**
 * Hook para obtener espaciado del tema actual
 */
export function useThemeSpacing() {
  const { getThemeValue } = useTheme();
  
  return {
    xs: getThemeValue('spacing.xs', '0.25rem'),
    sm: getThemeValue('spacing.sm', '0.5rem'),
    md: getThemeValue('spacing.md', '0.75rem'),
    lg: getThemeValue('spacing.lg', '1rem'),
    xl: getThemeValue('spacing.xl', '1.5rem'),
    xxl: getThemeValue('spacing.xxl', '2rem')
  };
}

export default ThemeProvider;